import "Components/UserManagementTable/UserManagementTable.less";
import { ColumnSortDirection, IColumn, Icon, Table } from "@clintonelec/react-storybook";
import { memo } from "react";
import { Waypoint } from "react-waypoint";
import { IUser } from "Interfaces";
import {
	fetchNextUserPageAction, selectFilteredUsers, selectUsersFetchable, selectUsersOptions
} from "Data/Redux/Slices/User";
import CreateUserModal from "Components/CreateUserModal";
import { UserRoles, UserStatus } from "Data/Objects/UI";
import { selectAllFilters } from "Data/Redux/Slices/Ui";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";

const UserManagementTable = () => {
	const users = useAppSelector(state => selectFilteredUsers(state, selectAllFilters(state)));
	const usersOptions = useAppSelector(selectUsersOptions);
	const filters = useAppSelector(selectAllFilters);
	const canFetchUsers = useAppSelector(selectUsersFetchable);
	const dispatch = useAppDispatch();
	const fetchNextUserPage = () => dispatch(fetchNextUserPageAction());

	const renderWaypoint = () => {
		if (!canFetchUsers) {
			return null;
		}

		return (
			<Waypoint
				onEnter={ fetchNextUserPage } key={ usersOptions[ "page[number]" ] } />
		);
	};

	const renderUsername = (user: IUser) => {
		return (
			<CreateUserModal user={ user }>
				<span className="selectable-text">{ user.username }</span>
			</CreateUserModal>
		);
	};

	const renderStatus = (user: IUser) => {
		if (user.active) {
			return <Icon name="check" fixedWidth className="active-status" />;
		}

		return <Icon name="xmark" fixedWidth className="inactive-status" />;
	};

	const getColumns = (): IColumn[] => {
		const { USER_STATUS } = filters;
		const columns = [
			{
				title: "User ID",
				dataIndex: "username",
				key: "username",
				sortable: true,
				defaultSortOrder: ColumnSortDirection.SORT_ASC
			},
			{
				title: "Name",
				dataIndex: "name",
				key: "name",
				sortable: true
			},
			{
				title: "Email",
				dataIndex: "email",
				key: "email",
				sortable: false
			},
			{
				title: "Role",
				dataIndex: "role",
				key: "role",
				sortable: false
			}
		];

		if (USER_STATUS === UserStatus.ALL_USERS) {
			columns.push({
				title: "Status",
				dataIndex: "status",
				key: "status",
				sortable: false
			});
		}

		return columns;
	};

	const dataSource = users?.flatMap((user) => {
		const { id, name, email, admin } = user;

		return {
			key: id,
			username: renderUsername(user),
			name: name,
			email: email,
			role: admin ? UserRoles.ADMIN : UserRoles.USER,
			status: renderStatus(user)
		};
	});

	return (
		<div className="table-wrapper">
			<Table
				data={ dataSource ?? [] }
				columns={ getColumns() }
			/>
			{ renderWaypoint() }
		</div>
	);
};

export default memo(UserManagementTable);
